import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import { Col, Container, Row, Button, Form } from "react-bootstrap";
import AddLocationModal from "../../../../../container/views/CenterRegistration/Modals/AddLocation";
import { allSharedComponents } from "../../../../../shared";
import {
  centerActions,
  centersActions,
  SpotActions,
} from "../../../../../redux/actions";
import {
  translationConstants,
  spotConstants,
  Directions,
  globalGridActions,
  centerStatusClasses,
  CenterSpotStatusEnum,
  GoogleMapConstants,
} from "../../../../../constants";
import { getCenterSpotStatus } from "../../../../AdminPortal/Views/Centers/utilities";
import GoogleMapView from "../../../../../shared/components/GoogleMap/GoogleMapView";
import { instructorActions } from "../../../../../redux/actions/instructor.action";
import RejectModal from "../Modals/RejectModal";
import { convertObjectToFormData } from "../../../../../system";
import { replaceSportsIdWithName } from "../../../../../system";

const { CustomModal, DataTableWithoutPagination, DeleteModal } =
  allSharedComponents;

const LocationsTab = ({ isAdmin, centerId }) => {
  const methods = useForm();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const formState = useSelector(
    (state) => state.centersReducer.centerProfileData
  );
  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );
  const isViewModalOpen = useSelector(
    (state) => state.InstructorReducer.isViewModalOpen
  );
  const spotDataById = useSelector((state) => state.SpotsReducer?.spotDataById);

  const [allowedSports, setAllowedSports] = useState([]);
  const [currentAction, setCurrentAction] = useState("");

  const containerStyle = {
    width: "100%",
    height: "300px",
  };

  const options = {
    zoomControl: true,
    clickableIcons: false,
    disableDefaultUI: true,
  };

  const [modalData, setModalData] = useState(null);
  const [message, setMessage] = useState("");
  const [spotToDelete, setSpotToDelete] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [existingSpotCoordinates, setExistingSpotCoordinates] = useState(null);

  const getRowData = (row, action) => {
    const filteredLatLongList = formState?.centerSpots
      ?.map((obj, index) => ({ ...obj, rowIndex: index }))
      ?.filter((obj) => obj.status !== CenterSpotStatusEnum.Rejected) //Filter out objects where status is not rejected
      .map((obj) => ({
        lat: obj.lat,
        long: obj.long,
        rowIndex: obj?.rowIndex,
      }));

    setExistingSpotCoordinates(filteredLatLongList);

    const updatedData = {
      spotTypeId: row.spotTypeId,
      services: row.services?.map(String),
      spotInfo: null,
      safetyDocuments: row.safetyDocuments?.map((doc) => ({
        path: doc.originalFileName,
      })),
      id: row.centerSpotId,
      spotName: row.spotName,
      operationalDocuments: row.operationalDocuments?.map((doc) => ({
        path: doc.originalFileName,
      })),
      rowIndex: row.rowIndex,
      sports: row.sportIds,
      disabled: true,
    };
    switch (action) {
      case globalGridActions.VIEW:
        setIsCenterSpotUpdate(false);
        setCurrentAction(globalGridActions.VIEW);
        dispatch(centerActions.setFormState(updatedData));
        setModalData({
          ...row,
          sports: row.sportIds,
          services: row.services.map(String),
          disabled: true,
          id: row.spotId,
          centerSpotSports: row.sportIds.map(String),
        });
        dispatch(SpotActions.getSpotById(row?.spotId));
        toggleAddLocationModal();
        break;

      case globalGridActions.EDIT:
        setIsCenterSpotUpdate(true);
        setCurrentAction(globalGridActions.EDIT);
        dispatch(centerActions.setFormState(updatedData));
        setModalData({
          ...row,
          sports: row.sportIds,
          services: row.services.map(String),
          disabled: false,
          id: row.spotId,
          centerSpotSports: row.sportIds.map(String),
        });
        dispatch(SpotActions.getSpotById(row?.spotId));
        dispatch(centersActions.getCenterSpotById(centerId));
        toggleAddLocationModal();
        break;

      case globalGridActions.DELETE:
        setCurrentAction(globalGridActions.DELETE);
        if (row.spotTypeId) {
          dispatch(SpotActions.deletePrivateSpot(row?.centerSpotId, centerId));
        } else {
          dispatch(SpotActions.deletePublicSpot(row?.centerSpotId, centerId));
        }
        setIsDeleteModalOpen(false);
        break;

      default:
        break;
    }
  };

  const [title, setTitle] = useState("");
  const [locations, setLocations] = useState([]);
  const [addLocationModal, setAddLocationModal] = useState(false);
  const [isCenterSpotUpdate, setIsCenterSpotUpdate] = useState(false);
  const [mapCenter, setMapCenter] = useState({
    lat: GoogleMapConstants.DEFAULT_LATITUDE,
    lng: GoogleMapConstants.DEFAULT_LONGITUDE,
  });

  const onDelete = (heading, row) => {
    setTitle(heading);
    setMessage(
      `${t(translationConstants.ARE_YOU_SURE)} ${row?.spotName} ${t(
        translationConstants.FROM_CENTER_SPOTS
      )}`
    );
    setSpotToDelete(row);
    setIsDeleteModalOpen(!isDeleteModalOpen);
  };

  const getColumns = (t, translationConstants) => [
    {
      id: "spotInfo.spotInfo.name",
      name: t(translationConstants.SPOT_NAME),
      selector: (row) => row.spotName || "",
    },
    {
      id: "spotTypeId",
      name: t(translationConstants.LOCATION_TYPE),
      selector: (row) =>
        `${
          row.spotTypeId === spotConstants.spotType.PUBLIC
            ? t(translationConstants.PUBLIC_SPOT)
            : t(translationConstants.PRIVATE_SPOT)
        }`,
    },
    {
      id: "sportIds",
      name: t(translationConstants.SPORTS),
      selector: (row) => replaceSportsIdWithName(row.sportIds, t),
    },
    {
      id: "status",
      name: t(translationConstants.STATUS),
      selector: (row) => (
        <span className={`capitalize-first ${centerStatusClasses[row.status]}`}>
          {t(getCenterSpotStatus(row.status))}
        </span>
      ),
    },
    {
      id: "actions",
      name: t(translationConstants.Actions),
      selector: (row, rowIndex) => (
        <div className="d-flex justify-content-center  align-items-center">
          <svg
            onClick={() =>
              getRowData({ ...row, rowIndex: rowIndex }, globalGridActions.VIEW)
            }
            width="20"
            height="20"
            // viewBox="0 0 50 16"
            viewBox={direction === Directions.rtl ? "0 0 20 16" : "0 0 20 16"}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.1685 8.99922C16.1685 8.99922 14.0341 13.268 9.05391 13.268C4.07371 13.268 1.93933 8.99922 1.93933 8.99922C1.93933 8.99922 4.07371 4.73047 9.05391 4.73047C14.0341 4.73047 16.1685 8.99922 16.1685 8.99922Z"
              stroke="#588930"
              strokeWidth="1.42292"
              strokeLinecap="round"
            />
            <circle
              cx="9.05381"
              cy="8.99766"
              r="2.13438"
              stroke="#588930"
              strokeWidth="1.42292"
              strokeLinecap="round"
            />
          </svg>
          {!isAdmin && (
            <>
              <svg
                onClick={() =>
                  getRowData(
                    { ...row, rowIndex: rowIndex },
                    globalGridActions.EDIT
                  )
                }
                width="20"
                height="20"
                viewBox={
                  direction === Directions.rtl ? "22 2 20 11" : "20 0 20 16"
                }
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M36.4449 13.2687V9.53359H35.3778V13.2687H26.8403V4.73125H30.5754V3.66406H26.8403C26.2509 3.66406 25.7731 4.14186 25.7731 4.73125V13.2687C25.7731 13.8581 26.2509 14.3359 26.8403 14.3359H35.3778C35.9671 14.3359 36.4449 13.8581 36.4449 13.2687ZM34.9392 4.05008C34.6946 3.80355 34.3597 3.66406 34.0103 3.66406C33.6614 3.66406 33.3268 3.80324 33.0808 4.05072L28.6153 8.51625C28.2162 8.86785 27.9546 9.39104 27.9092 9.95369L27.9074 11.6692V12.2028H30.1136C30.7189 12.1613 31.2472 11.8971 31.6199 11.4672L36.0602 7.02882C36.3066 6.78247 36.445 6.44835 36.445 6.09996C36.445 5.75157 36.3066 5.41745 36.0602 5.1711L34.9392 4.05008ZM30.076 11.1359C30.3611 11.1157 30.6264 10.9831 30.8395 10.7393L34.075 7.50381L32.6054 6.03405L29.3461 9.29222C29.1299 9.48342 28.9962 9.75092 28.9747 9.99562V11.1349L30.076 11.1359ZM33.3601 5.27956L34.8296 6.7492L35.3057 6.27316C35.3519 6.22695 35.3778 6.16427 35.3778 6.09892C35.3778 6.03357 35.3519 5.97089 35.3057 5.92468L34.1832 4.80217C34.1375 4.75611 34.0753 4.73021 34.0104 4.73021C33.9455 4.73021 33.8833 4.75611 33.8376 4.80217L33.3601 5.27956Z"
                  fill="#2C7465"
                />
              </svg>
              <svg
                onClick={() => {
                  onDelete(translationConstants.DELETE_A_SPOT, {
                    ...row,
                    rowIndex: rowIndex,
                  });
                }}
                width="20"
                height="20"
                viewBox={
                  direction === Directions.rtl ? "40 0 20 16" : "40 0 20 16"
                }
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ overflow: "hidden" }}
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M48.7769 2.47656H52.3342C52.9891 2.47656 53.5199 3.00745 53.5199 3.66233V4.25521H55.2987C55.9536 4.25521 56.4845 4.78609 56.4845 5.44097V6.62674C56.4845 7.28162 55.9536 7.8125 55.2987 7.8125H55.2512L54.7059 14.3344C54.7059 14.9893 54.175 15.5201 53.5201 15.5201H47.5913C46.9364 15.5201 46.4055 14.9893 46.4076 14.3836L45.86 7.8125H45.8126C45.1577 7.8125 44.6268 7.28162 44.6268 6.62674V5.44097C44.6268 4.78609 45.1577 4.25521 45.8126 4.25521H47.5911V3.66233C47.5911 3.00745 48.122 2.47656 48.7769 2.47656ZM47.5911 5.44097V5.44092H45.8126V6.62668H55.2987V5.44092H53.5199V5.44097H47.5911ZM47.5913 14.3344L47.0497 7.81268H54.0615L53.5222 14.2851L53.5201 14.3344H47.5913ZM52.3345 3.66243V4.25532H48.7772V3.66243H52.3345Z"
                  fill="#FF0000"
                />
              </svg>
            </>
          )}
        </div>
      ),
    },
  ];

  const [columns, setColumns] = useState(getColumns(t, translationConstants));
  const [isApprove, setIsApprove] = useState(false);
  useEffect(() => {
    const updatedColumns = getColumns(t, translationConstants);
    setColumns(updatedColumns);
  }, [t, translationConstants]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = (data) => {
    // finalSubmission();
  };

  const toggleAddLocationModal = (isAdd) => {
    if (isAdd) {
      setIsCenterSpotUpdate(false);
      setCurrentAction(globalGridActions.ADD);
      setModalData(null);
      const updatedFormState = {
        sports: formState?.sportIds?.map(String),
      };
      dispatch(centerActions.setFormState(updatedFormState));
      const filteredLatLongList = formState?.centerSpots
        ?.map((obj, index) => ({ ...obj, rowIndex: index }))
        ?.filter((obj) => obj.status !== 1) //Filter out objects where status is not rejected
        .map((obj) => ({
          lat: obj.lat,
          long: obj.long,
          rowIndex: obj?.rowIndex,
        }));

      setExistingSpotCoordinates(filteredLatLongList);
    }
    dispatch(SpotActions.setPrivateSpotReducerNull());
    setAddLocationModal((prev) => !prev);
  };

  const onLocationSubmit = (dataToSend) => {
    const updatedSpots =
      formState && Array.isArray(formState.spots) ? formState.spots : [];

    //Spread the existing spots array and add the new dataToSend
    //dataToSend = { ...formState, spots: [...updatedSpots, dataToSend] };
    dispatch(centerActions.setFormState(dataToSend));

    const index = dataToSend.rowIndex;
    const services = dataToSend.services;
    const spotsToSend = [...updatedSpots, dataToSend];
    dataToSend = {
      spots: spotsToSend?.map((spot) => ({
        ...spot,
        safetyDocuments:
          spot.safetyDocuments?.length > 0
            ? spot.safetyDocuments[0]
            : undefined,
        operationalDocuments:
          spot.operationalDocuments?.length > 0
            ? spot.operationalDocuments[0]
            : undefined,
      })),
    };
    var formData = convertObjectToFormData(dataToSend);

    if (currentAction === globalGridActions.EDIT) {
      if (dataToSend?.spots[0]?.spotTypeId === 0) {
        const dataToUpdate = dataToSend?.spots[0];
        const centerSpotSports = dataToUpdate?.centerSpotSports;

        const sportIdsUpdated = Array.isArray(centerSpotSports)
          ? centerSpotSports.map(Number)
          : [Number(centerSpotSports)];

        const extractedData = {
          id: formState?.centerSpots[index]?.centerSpotId,
          sportIds: sportIdsUpdated,
          serviceIds: dataToUpdate?.services?.map(Number),
        };
        dispatch(
          centersActions.editPublicCenterSpot(
            extractedData,
            centerId,
            setAddLocationModal
          )
        );
      } else {
        const spot = {
          spotInfo: dataToSend?.spots[0]?.spotInfo?.spotInfo,
          sportIds:
            dataToSend?.spots[0]?.spotInfo?.spotInfo?.sports?.map(Number),
          id: spotDataById?.id,
          centers: spotDataById?.centers,
          centersAvailable: spotDataById?.centersAvailable,
        };

        const centerSpot = {
          id: formState?.centerSpots[index]?.centerSpotId,
          sportIds: Array.isArray(dataToSend?.spots[0]?.centerSpotSports)
            ? dataToSend.spots[0].centerSpotSports?.map(Number)
            : [Number(dataToSend.spots[0]?.centerSpotSports)],
          serviceIds: services.map(Number),
        };

        const payload = {
          spot,
          centerSpot,
        };

        dispatch(
          centersActions.editPrivateCenterSpot(
            payload,
            centerId,
            setAddLocationModal
          )
        );
      }
    } else {
      dispatch(
        centersActions.addCenterSpot(formData, centerId, setAddLocationModal)
      );
    }
  };

  useEffect(() => {
    //Set initial form values from selected lat lng
    setMapCenter({
      lat: parseFloat(formState?.centerSpots[0]?.lat),
      lng: parseFloat(formState?.centerSpots[0]?.long),
    });

    // eslint-disable-next-line
  }, [formState]);

  useEffect(() => {
    const transformedData = formState?.centerSpots
      .filter((spot) => spot.status === CenterSpotStatusEnum.Approved) // Filter spots with status Approved
      .map((spot) => ({
        latitude: parseFloat(spot.lat),
        longitude: parseFloat(spot.long),
        locationName: spot?.spotName,
        locationArabicName: spot?.spotArabicName,
        spotTypeId: spot.spotTypeId,
        sports: spot.sportIds,
        isPublic: spot.spotTypeId,
      }));

    setLocations(transformedData);
    // eslint-disable-next-line
  }, [formState]);

  const handleApproveCenterSpot = (data) => {
    setIsApprove(true);
    const dataToSend = {
      centerSpotId: data.centerSpotId,
      centerEmail: formState?.centerUserInfo[0]?.email,
      status: 0,
    };
    dispatch(
      centersActions.approveCenterSpot(
        dataToSend,
        centerId,
        setAddLocationModal
      )
    );
  };

  const onSetShow = (heading) => {
    setTitle(heading);
    dispatch(instructorActions.toggleViewModal(!isViewModalOpen));
  };

  const openRejectSpotModal = () => {
    onSetShow(translationConstants.REJECT_SPOT);
  };

  useEffect(() => {
    const body = {
      length: null,
    };
    dispatch(SpotActions.GetAllPublicSpots(body));
  }, []);

  return (
    <>
      <div className="px-4 pt-4 mx-2">
        <GoogleMapView
          locations={locations}
          containerStyle={containerStyle}
          options={options}
          mapCenter={mapCenter}
        />
      </div>
      <div className="px-4 py-3 mx-2">
        <span className="my-5 fs-14">
          {t(
            translationConstants.THE_LOCATION_PINS_SHOWN_ON_MAP_ARE_ONLY_APPROVED_LOCATIONS
          )}
        </span>
      </div>
      <FormProvider {...methods}>
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Container className="reset-max-width">
            <Row>
              {!isAdmin && (
                <Col
                  md={12}
                  className="d-flex justify-content-end py-0 px-3 mb-2"
                >
                  <Button
                    className="Registeration-button m-0"
                    type="button"
                    onClick={() => toggleAddLocationModal(true)}
                  >
                    <svg
                      width="13"
                      height="14"
                      viewBox="0 0 13 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.6239 7.87686L0.305033 7.87686L0.305033 6.12465L5.6239 6.12465L5.6239 0.805788H7.3761L7.3761 6.12465L12.695 6.12465L12.695 7.87686L7.3761 7.87686L7.3761 13.1957H5.6239L5.6239 7.87686Z"
                        fill="white"
                      />
                    </svg>
                    &nbsp;
                    {t(translationConstants.ADD_LOCATION)}
                  </Button>
                </Col>
              )}
              <h5 className="fw-bolder px-4 mb-0">
                {t(translationConstants.LOCATION_SPOTS)}
              </h5>
              <div className="px-4">
                <hr className="mt-1 mb-2"></hr>
              </div>
              <Col md={12} className="px-4">
                <DataTableWithoutPagination
                  columns={columns}
                  data={formState?.centerSpots}
                />
              </Col>
            </Row>
          </Container>
        </Form>
      </FormProvider>

      <CustomModal
        size="lg"
        scrollable
        show={addLocationModal}
        customClass="custom-modal"
        setShow={toggleAddLocationModal}
      >
        <AddLocationModal
          onSubmit={onLocationSubmit}
          modalData={modalData}
          allowedSports={allowedSports}
          handleApproveCenterSpot={handleApproveCenterSpot}
          openRejectSpotModal={openRejectSpotModal}
          isCenterUser
          isCenterSpotUpdate={isCenterSpotUpdate}
          currentAction={currentAction}
          isApprove={isApprove}
          existingSpotCoordinates={existingSpotCoordinates}
        />
      </CustomModal>

      <CustomModal
        scrollable={true}
        size="lg"
        setShow={onSetShow}
        show={isViewModalOpen}
        customClass="view-user-modal"
      >
        <RejectModal
          title={title}
          centerId={centerId}
          formState={formState}
          modalData={modalData}
          setAddLocationModal={setAddLocationModal}
          isRejectCenterSpot
        />
      </CustomModal>

      <CustomModal
        scrollable={true}
        size="md"
        setShow={setIsDeleteModalOpen}
        show={isDeleteModalOpen}
        customClass="view-user-modal"
      >
        <DeleteModal
          title={title}
          message={message}
          dataToDelete={spotToDelete}
          removeRowData={getRowData}
          onCancel={() => setIsDeleteModalOpen(false)}
        />
      </CustomModal>
    </>
  );
};

export default LocationsTab;
